
/* Copyright (c) 2017 Backpack Health LLC */
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';

import Card, { CardSection } from './Card';
import { CollapsibleCard } from './CollapsibleCard';
import PolicyText from '../PolicyText.connected';
import LegalDocumentLinks from '../LegalDocumentLinks';
import { TextLink } from '../TextLink';
import { withIntl } from '../../../utilities/localizedUtil';
import { ProfileThemeMarkdown } from '../Markdown';
import Spacer from '../Spacer';
import PrivacyHeader from '../PrivacyHeader';
import { getActiveProfile, getMainProfileId } from '../../appAuthorized/redux/reduxSelectors';

interface Props {
  intl: any;
  allowWithdraw: boolean;
  onWithdrawConsent?(): void;
  customWithdrawConsent?: any;
  isCollapsible: boolean;
  defaultCollapsed: boolean;
  onClickPrivacyLink?(): void;
  title: string;
  subTitle?: string;
  summary: string;
  name?: string;
}

@withIntl
class PrivacyCardBasedOnPolicyName extends React.Component<Props, {}> {
  render() {
    const { name, intl } = this.props;
    const languageCode = intl.locale;

    return (
      <PolicyText name={name} languageCode={languageCode}>
        {
          ({ title, summary }) => (
            <PrivacyCard {...this.props} title={title} summary={summary} />
          )
        }
      </PolicyText>
    );
  }
}

class PrivacyCard extends React.Component<Props, {}> {
  static defaultProps: Partial<Props> = {
    defaultCollapsed: true,
    isCollapsible: true,
  };

  render() {
    const { allowWithdraw, onWithdrawConsent, customWithdrawConsent, isCollapsible, defaultCollapsed, onClickPrivacyLink, title, subTitle, summary } = this.props;

    return (
      <CollapsibleCard
        title={<PrivacyHeader title={title} subTitle={subTitle} />}
        isCollapsible={isCollapsible}
        defaultCollapsed={defaultCollapsed}
      >
        <CardSection hasBorder={false}>
          <ProfileThemeMarkdown text={summary} />

          {
            (allowWithdraw && !!onWithdrawConsent) && (
              <>
                <Spacer size="12" />
                <TextLink className="font-subhead font-color-gsMiddle" style={{ display: 'block', marginBottom: 18, textDecoration: 'underline' }} onClick={onWithdrawConsent}>
                  <FormattedMessage id="privacy.dontConsent.button.label" />
                </TextLink>
              </>
            )
          }

          {
            customWithdrawConsent && (
              <div style={{ display: 'block', marginTop: 18, textDecoration: 'underline' }}>
                {customWithdrawConsent}
              </div>
            )
          }
          <Spacer size="9" />

          <LegalDocumentLinks.PrivacyPolicy className="font-footnote font-color-rb-dark-accent" onClick={onClickPrivacyLink}>
            <FormattedMessage id="privacy.seeFullPrivacyPolicy.button.label" />
          </LegalDocumentLinks.PrivacyPolicy>
          <Spacer size="2" />
          <div className="font-caption-1 font-color-gsMiddle">
            <FormattedMessage id="branding.tagline.healthinfo" />
          </div>
        </CardSection>
      </CollapsibleCard>
    );
  }
}

function mapStateToProps(state) {
  const activeProfile = getActiveProfile(state);
  const mainProfileId = getMainProfileId(state);
  const isMainProfile = activeProfile.data.id === mainProfileId;

  return {
    allowWithdraw: isMainProfile || !activeProfile.hasUser,
  };
}

const ConnectedPrivacyCard = connect(mapStateToProps)(PrivacyCardBasedOnPolicyName);

export const ConnectedWhitePrivacyCard = (props) => (
  <Card className="bkgd-color-gsLightest" hasDropshadow={true}>
    <ConnectedPrivacyCard {...props} />
  </Card>
);

export const ConnectedGrayPrivacyCard = (props) => (
  <Card className="bkgd-color-gsLight" hasDropshadow={false}>
    <ConnectedPrivacyCard {...props} />
  </Card>
);

export const WhitePrivacyCard = (props) => (
  <Card className="bkgd-color-gsLightest" hasDropshadow={true}>
    <PrivacyCardBasedOnPolicyName {...props} />
  </Card>
);

export const GrayPrivacyCard = (props) => (
  <Card className="bkgd-color-gsLight" hasDropshadow={false}>
    <PrivacyCardBasedOnPolicyName {...props} />
  </Card>
);

export default PrivacyCard;
